import { AbstractControl, FormGroup } from '@angular/forms';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { InputControlBase } from '../../forms/input-control.base';
import { LocalDate } from '../../../model/local-date';
import { AstralFieldActionComponent } from '../astral-field-action/astral-field-action.component';
import { AstralFieldShellComponent } from '../astral-field-shell/astral-field-shell.component';
import { FormControlEx, toErrorMessage } from '../../forms/form-control';
import { FormControlExFactory } from '../../../service/factory/form-control-ex-factory.service';
import { resolveAstralIconClass } from '../astral-icon-class.util';
import { shouldShowAstralValidationError } from '../astral-validation-state';
import { AstralFieldAccessibilityDirective } from '../astral-field-accessibility.util';
import { ASTRAL_MAX_DATE, astralSupportedYearValidator } from '../astral-date-validation';
import {
  normalizeAstralTemporalInput,
  parseAstralDateInput,
} from '../astral-temporal-input/astral-temporal-input.util';
import { DANISH, FINNISH, LanguageService, SWEDISH } from '../../../service/language.service';

export function getAstralDatePlaceholder(language: string | null | undefined): string {
  switch (language) {
    case SWEDISH:
    case DANISH:
      return 'ÅÅÅÅ-MM-DD';
    case FINNISH:
      return 'VVVV-KK-PP';
    default:
      return 'YYYY-MM-DD';
  }
}

@Component({
  selector: 'astral-date-picker',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    AstralFieldActionComponent,
    AstralFieldShellComponent,
    AstralFieldAccessibilityDirective,
  ],
  templateUrl: './astral-date-picker.component.html',
  styleUrls: ['./astral-date-picker.component.scss'],
})
export class AstralDatePickerComponent extends InputControlBase implements AfterViewInit {
  private static nextId = 0;
  private readonly uid = `astral-date-picker-${++AstralDatePickerComponent.nextId}`;
  private updatingLegacyGroup = false;
  readonly maxDate = ASTRAL_MAX_DATE;

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly controlFactory: FormControlExFactory,
    private readonly languageService: LanguageService
  ) {
    super();
  }

  @Input() withShell = true;
  @Input() clearOthers = false;
  @Input() filterActions = false;
  @Input() doNotDisplayClear = false;
  @Input() inputId: string;
  @Input() inputName: string;
  @Input() label: string;
  @Input() span: number | 'full' = 1;
  @Input() focused = false;
  @Input() showEmptyReadOnly = false;
  @Input() emptyIsNull = false;
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() model: LocalDate;
  @Input() isRequired = false;

  @Output() readonly addonClick = new EventEmitter();
  @Output() readonly clearClick = new EventEmitter();

  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChild('calendarInput') calendarInput: ElementRef<HTMLInputElement>;

  @HostBinding('style.grid-column')
  get hostGridColumn(): string | null {
    if (this.span === 'full') {
      return '1 / -1';
    }

    if (!this.span || this.span <= 1) {
      return null;
    }

    return `span ${this.span}`;
  }

  isFocused = false;
  inputText = '';

  get display(): boolean {
    if (this.showEmptyReadOnly) {
      return true;
    }

    return (this.resolvedLocalDate?.hasValue() ?? false) || !this.resolvedReadonly;
  }

  get inputValue(): string {
    return this.resolvedLocalDate?.hasValue() ? this.resolvedLocalDate.toISOString() : '';
  }

  get displayedInputValue(): string {
    return this.isFocused ? this.inputText : this.inputValue;
  }

  get datePlaceholder(): string {
    return getAstralDatePlaceholder(this.languageService.currentLanguage);
  }

  get invalid(): boolean {
    return !!this.errorMessage && shouldShowAstralValidationError(this.rawControl);
  }

  get resolvedDisabled(): boolean {
    return this.disabled ?? this.rawControl?.disabled ?? false;
  }

  get resolvedInputId(): string {
    return this.inputId ?? this.uid;
  }

  get resolvedInputName(): string {
    if (this.inputName) {
      return this.inputName;
    }

    const source = this.shellControl?.caption ?? this.label;
    const caption = source
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return caption || this.uid;
  }

  get resolvedLabel(): string | null {
    return this.label ?? this.shellControl?.caption ?? null;
  }

  get resolvedReadonly(): boolean {
    if (this.readonly) {
      return true;
    }

    if (this.legacyGroup) {
      return this.legacyDateControls.every(control => control?.isReadonly) || this.legacyGroup.disabled;
    }

    return this.shellControl?.isReadonly ?? false;
  }

  get readonlyIconClass(): string {
    return resolveAstralIconClass('lock') ?? '';
  }

  get resolvedRequired(): boolean {
    if (this.isRequired) {
      return true;
    }

    return this.legacyDateControls.some(control => control?.required) || this.shellControl?.required || false;
  }

  get showClearOthers(): boolean {
    const parentControls = this.rawControl?.parent?.controls;

    if (this.resolvedDisabled || this.resolvedReadonly || !this.clearOthers || !parentControls) {
      return false;
    }

    return Object.keys(parentControls).length > 1 && (this.filterActions || !!this.inputValue);
  }

  get showClearThis(): boolean {
    return !!this.rawControl && !this.resolvedDisabled && !this.resolvedReadonly && !this.doNotDisplayClear && (this.filterActions || !!this.inputValue);
  }

  @HostListener('focus')
  focusHost() {
    this.input?.nativeElement.focus();
  }

  get rawControl(): AbstractControl | null {
    return this.control as unknown as AbstractControl | null;
  }

  private get legacyGroup(): FormGroup | null {
    const control = this.rawControl;
    return control instanceof FormGroup ? control : null;
  }

  private get legacyDateControls(): FormControlEx[] {
    const group = this.legacyGroup;
    if (!group) {
      return [];
    }

    return ['year', 'month', 'day']
      .map(name => group.get(name) as FormControlEx | null)
      .filter((control): control is FormControlEx => !!control);
  }

  private get shellControl(): FormControlEx | null {
    if (this.legacyGroup) {
      return this.legacyDateControls[0] ?? null;
    }

    return this.control;
  }

  private get resolvedLocalDate(): LocalDate {
    if (this.legacyGroup) {
      this.ensureLegacyControls();
      return this.getLegacyGroupDateValue();
    }

    return LocalDate.fromDate(this.control?.value);
  }

  ngOnInit(): void {
    this.ensureLegacyControls();
    this.inputText = this.inputValue;
    this.rawControl?.addValidators(astralSupportedYearValidator);
    this.rawControl?.updateValueAndValidity({ emitEvent: false });
  }

  override ngOnDestroy(): void {
    this.rawControl?.removeValidators(astralSupportedYearValidator);
    this.rawControl?.updateValueAndValidity({ emitEvent: false });
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    if (this.focused) {
      this.input?.nativeElement.focus();
      this.isFocused = true;
      this.cd.detectChanges();
    }
  }

  clearAllOtherInputs(): boolean {
    const parentControls = this.rawControl?.parent?.controls;
    if (!parentControls) {
      return false;
    }

    for (const controlName of Object.keys(parentControls)) {
      const currentControl = this.rawControl.parent.get(controlName);
      if (currentControl !== this.rawControl) {
        currentControl.setValue(undefined);
      }
    }

    this.rawControl.parent.markAsDirty();
    this.addonClick.emit(this.rawControl);
    return false;
  }

  clearInput(): boolean {
    if (!this.rawControl) {
      return false;
    }

    this.commitInputValue('');
    this.clearClick.emit(this.rawControl);
    return false;
  }

  handleBlur(): void {
    this.isFocused = false;
    const normalized = this.inputText ? normalizeAstralTemporalInput(this.inputText, 'date') : '';
    if (normalized) {
      this.commitInputValue(normalized);
    } else if (this.inputText) {
      // Do not leave a malformed draft disconnected from a valid form value.
      // The text control owns editing, while the model keeps the last valid date.
      this.inputText = this.inputValue;
    }
    this.onblur();
  }

  handleTextInput(value: string): void {
    this.inputText = value;
    if (!value.trim()) {
      this.commitInputValue('');
      return;
    }

    const normalized = normalizeAstralTemporalInput(value, 'date');
    if (normalized) {
      // Keep the user's text and caret intact while editing. The normalized
      // value is applied to the model now and rendered only on blur.
      this.handleDateChange(normalized);
    }
  }

  handleCalendarChange(value: string): void {
    if (value) {
      this.commitInputValue(value);
      this.input?.nativeElement.focus();
    }
  }

  openCalendar(): void {
    if (this.resolvedDisabled || this.resolvedReadonly || !this.calendarInput) {
      return;
    }

    const calendarInput = this.calendarInput.nativeElement;
    calendarInput.value = this.inputValue;

    if (typeof calendarInput.showPicker === 'function') {
      calendarInput.showPicker();
    } else {
      calendarInput.click();
    }
  }

  handleDateChange(value: string): void {
    const parts = value ? parseAstralDateInput(value) : null;
    let localDateValue = parts ? new LocalDate(parts.year, parts.month, parts.day) : LocalDate.None;

    if (!localDateValue?.hasValue() && (this.emptyIsNull || this.resolvedRequired)) {
      localDateValue = null;
    }

    if (this.legacyGroup) {
      const previousValue = this.getLegacyGroupDateValue();
      this.setLegacyGroupDateValue(localDateValue);
      this.legacyGroup.markAsDirty();
      // Native date inputs only emit a change for a complete value or a clear.
      // Treat either as an interaction so required legacy date groups can show
      // their own validation feedback without relying on a later blur event.
      this.legacyGroup.markAsTouched();
      this.legacyDateControls.forEach(control => control.markAsTouched());
      this.valueChange.emit({
        formSnapshot: this.legacyGroup.parent ? Object.assign({}, this.legacyGroup.parent.value) : Object.assign({}, this.legacyGroup.value),
        newValue: localDateValue,
        previousValue,
      });
      return;
    }

    this.control?.setValue(localDateValue);
    this.control?.markAsDirty();
  }

  handleFocus(): void {
    this.isFocused = true;
    this.inputText = this.inputValue;
    this.onfocus();
  }

  override onblur(): void {
    if (this.legacyGroup) {
      this.legacyGroup.markAsTouched();
      this.legacyDateControls.forEach(control => control.markAsTouched());
      return;
    }

    this.control?.markAsTouched();
    super.onblur();
  }

  get errorMessage(): { message: string; params: any } {
    if (this.shouldShowLegacyControlError(this.shellControl) && this.shellControl?.errors) {
      return this.shellControl.getErrorMessage();
    }

    if (shouldShowAstralValidationError(this.legacyGroup) && this.legacyGroup.errors) {
      return toErrorMessage(this.legacyGroup.errors);
    }

    if (shouldShowAstralValidationError(this.control) && this.control?.errors) {
      return this.control.getErrorMessage();
    }

    const parentError = this.getParentGroupError();
    if (parentError && this.isTouched(this.rawControl)) {
      return parentError;
    }

    return null;
  }

  private shouldShowLegacyControlError(control: FormControlEx | null): boolean {
    if (!control?.invalid) {
      return false;
    }

    // A report submit marks the legacy date group touched. Its generated
    // year/month/day controls may not receive that state until a later input event.
    return shouldShowAstralValidationError(control) || !!this.legacyGroup?.touched;
  }

  private getParentGroupError(): { message: string; params: any } | null {
    const parentForm = this.rawControl?.parent as any;
    if (!parentForm?.errors) {
      return null;
    }

    const controlName = Object.keys(parentForm.controls).find(key => parentForm.controls[key] === this.rawControl);
    const error = controlName ? parentForm.errors[controlName] : null;
    return error?.message ? { message: error.message, params: error.params ?? {} } : null;
  }

  private ensureLegacyControls(): void {
    const group = this.legacyGroup;
    if (!group) {
      return;
    }

    const initialValue = this.getInitialLegacyValue();
    const controlsToAdd: Array<{ name: 'year' | 'month' | 'day'; caption: string; value: number | null }> = [
      { name: 'year', caption: 'COMMON.YEAR', value: initialValue.year },
      { name: 'month', caption: 'COMMON.MONTH', value: initialValue.month },
      { name: 'day', caption: 'COMMON.DAY', value: initialValue.day },
    ];

    for (const entry of controlsToAdd) {
      if (!group.get(entry.name)) {
        group.addControl(
          entry.name,
          this.controlFactory.createWithOptions(entry.caption, entry.value, { required: this.isRequired })
        );
      }
    }

    this.syncLegacyControlState();
  }

  private getInitialLegacyValue(): LocalDate {
    const groupValue = this.getLegacyGroupDateValue(false);
    if (groupValue.hasValue()) {
      return groupValue;
    }

    if (this.model) {
      return LocalDate.fromDate(this.model);
    }

    return LocalDate.None;
  }

  private getLegacyGroupDateValue(ensureControls = true): LocalDate {
    const group = this.legacyGroup;
    if (!group) {
      return LocalDate.None;
    }

    if (ensureControls) {
      this.ensureLegacyControls();
    }

    const year = group.get('year')?.value ?? null;
    const month = group.get('month')?.value ?? null;
    const day = group.get('day')?.value ?? null;
    return new LocalDate(year, month, day);
  }

  private setLegacyGroupDateValue(localDateValue: LocalDate | null): void {
    const group = this.legacyGroup;
    if (!group) {
      return;
    }

    this.ensureLegacyControls();

    const value = localDateValue && localDateValue.hasValue() ? localDateValue : LocalDate.None;
    if (this.model === null || this.model === undefined) {
      this.model = value;
    } else {
      this.model.setDate(value.hasValue() ? value.toDate() : null);
    }

    try {
      this.updatingLegacyGroup = true;
      group.setValue({
        year: value.year,
        month: value.month,
        day: value.day,
      });
    } finally {
      this.updatingLegacyGroup = false;
    }
  }

  private syncLegacyControlState(): void {
    const group = this.legacyGroup;
    if (!group) {
      return;
    }

    this.legacyDateControls.forEach(control => {
      control.required = this.isRequired;
      control.isReadonly = this.resolvedReadonly;
    });

    if (this.resolvedReadonly) {
      group.disable({ emitEvent: false });
    } else {
      group.enable({ emitEvent: false });
    }
  }

  private isTouched(control: AbstractControl | null | undefined): boolean {
    return !!control?.touched;
  }

  private commitInputValue(value: string): void {
    this.inputText = value;
    this.handleDateChange(value);
  }
}
